import { motion, useReducedMotion, type Transition } from "framer-motion";
import { useLayoutEffect, useRef, useState, type Ref } from "react";

import MenuIcon from "@/blueprint/components/NavPill/MenuIcon";
import { MotionTransition } from "@/blueprint/tokens/Motion";
import {
  DESKTOP_NAV_CART_WIDTH_PX,
  DesktopNavLinks,
} from "@/chrome/SiteNav/DesktopNav";
import { cn } from "@/lib/utils";

type FloatingNavSurfaceProps = {
  expanded: boolean;
  cartCount: number;
  onCartClick: () => void;
  onMenuClick: () => void;
  menuButtonRef?: Ref<HTMLButtonElement>;
  onNavigate: () => void;
  onDismiss: () => void;
  onEscape: () => void;
};

type SurfaceWidths = {
  collapsed: number;
  expanded: number;
};

const readCssLength = (value: string) => Number.parseFloat(value) || 0;

const FloatingNavSurface = ({
  expanded,
  cartCount,
  onCartClick,
  onMenuClick,
  menuButtonRef,
  onNavigate,
  onDismiss,
  onEscape,
}: FloatingNavSurfaceProps) => {
  const navRootRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const contentRowRef = useRef<HTMLDivElement>(null);
  const menuLayerRef = useRef<HTMLDivElement>(null);
  const linksLayerRef = useRef<HTMLDivElement>(null);
  const rightControlsRef = useRef<HTMLDivElement>(null);
  const hasMeasuredRef = useRef(false);
  const [surfaceWidths, setSurfaceWidths] = useState<SurfaceWidths | null>(
    null,
  );
  const reducedMotion = useReducedMotion() === true;
  const morphTransition: Transition = reducedMotion
    ? { duration: 0 }
    : {
        duration: MotionTransition.navMorph.durationMs / 1000,
        ease: [...MotionTransition.navMorph.ease],
      };

  useLayoutEffect(() => {
    const measure = () => {
      const surface = surfaceRef.current;
      const contentRow = contentRowRef.current;
      const menuLayer = menuLayerRef.current;
      const linksLayer = linksLayerRef.current;
      const rightControls = rightControlsRef.current;
      const shopControl =
        rightControls?.querySelector<HTMLElement>(
          "[data-nav-control='shop']",
        ) ?? null;

      if (
        surface === null ||
        contentRow === null ||
        menuLayer === null ||
        linksLayer === null ||
        rightControls === null ||
        shopControl === null
      ) {
        return;
      }

      if (surface.getClientRects().length === 0) {
        // Hidden (display:none, e.g. across the sd boundary via resize/zoom).
        // Keep the last measured widths — nulling them left renders with no
        // width target, so a dismissal mid-hide stranded the surface at its
        // stale expanded width while the content crossfaded back to the menu
        // (the "wide blank pill" bug). Only force the next visible measure to
        // apply without animation.
        hasMeasuredRef.current = false;
        return;
      }

      const surfaceStyle = getComputedStyle(surface);
      const contentStyle = getComputedStyle(contentRow);
      const controlsStyle = getComputedStyle(
        rightControls.firstElementChild ?? rightControls,
      );
      const horizontalPadding =
        readCssLength(surfaceStyle.paddingLeft) +
        readCssLength(surfaceStyle.paddingRight);
      const contentGap = readCssLength(contentStyle.columnGap);
      const controlsGap = readCssLength(controlsStyle.columnGap);
      const rightWidth =
        shopControl.getBoundingClientRect().width +
        controlsGap +
        (cartCount > 0 ? DESKTOP_NAV_CART_WIDTH_PX : 0);
      const nextWidths = {
        collapsed:
          horizontalPadding +
          menuLayer.getBoundingClientRect().width +
          contentGap +
          rightWidth,
        expanded:
          horizontalPadding +
          linksLayer.getBoundingClientRect().width +
          contentGap +
          rightWidth,
      };

      setSurfaceWidths((current) =>
        current?.collapsed === nextWidths.collapsed &&
        current.expanded === nextWidths.expanded
          ? current
          : nextWidths,
      );
    };

    measure();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(measure);
    const shopControl = rightControlsRef.current?.querySelector<HTMLElement>(
      "[data-nav-control='shop']",
    );
    [menuLayerRef.current, linksLayerRef.current, shopControl].forEach(
      (element) => {
        if (element !== null && element !== undefined)
          observer.observe(element);
      },
    );

    return () => observer.disconnect();
  }, [cartCount]);

  useLayoutEffect(() => {
    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLElement)) return;

    if (expanded) {
      if (menuLayerRef.current?.contains(activeElement)) {
        linksLayerRef.current
          ?.querySelector<HTMLElement>("[data-nav-control='menu']")
          ?.focus();
      }
      return;
    }

    if (
      navRootRef.current?.contains(activeElement) &&
      !menuLayerRef.current?.contains(activeElement) &&
      !rightControlsRef.current?.contains(activeElement)
    ) {
      menuLayerRef.current
        ?.querySelector<HTMLButtonElement>("[data-nav-control='menu']")
        ?.focus();
    }
  }, [expanded]);

  useLayoutEffect(() => {
    if (surfaceWidths !== null) hasMeasuredRef.current = true;
  }, [surfaceWidths]);

  const targetWidth = surfaceWidths?.[expanded ? "expanded" : "collapsed"];
  const effectiveMorphTransition =
    surfaceWidths !== null && !hasMeasuredRef.current
      ? { duration: 0 }
      : morphTransition;

  return (
    <DesktopNavLinks
      containerRef={navRootRef}
      collapsed={!expanded}
      settleAfterCollapseChange={false}
      onNavigate={onNavigate}
      onDismiss={onDismiss}
      onEscape={onEscape}
    >
      {({ links, rightControls, linkHighlight }) => (
        // Must outrank NavHoverPanel's z-[5], or its overhang swallows link hovers.
        <div className="relative isolate z-10 inline-block">
          {/* Solid white backdrop (the pill is never translucent) on its own
              negative-z layer outside the clipping width-morph surface, so its
              4px optical overhang can paint. Light-scoped so its controls stay
              dark-on-white over dark pages. */}
          <div
            aria-hidden
            className="absolute -inset-1 -z-10 rounded-full bg-brand-white"
          />
          <motion.div
            ref={surfaceRef}
            initial={false}
            {...(targetWidth === undefined
              ? {}
              : { animate: { width: targetWidth } })}
            transition={effectiveMorphTransition}
            /* The width must ALSO be the base style: with initial={false}, a
               measurement landing before framer's first commit reads as the
               element's initial state, so framer never writes the width and the
               pill paints at its natural (expanded) size — the phantom wide
               blank pill. React owning the base width makes that impossible;
               framer still animates changes on top. */
            style={
              targetWidth === undefined
                ? { visibility: "hidden" }
                : { width: targetWidth }
            }
            data-theme="light"
            className="relative overflow-hidden rounded-full p-1"
          >
            {/* Inside the morph surface so it paints over the pill's white
                backdrop and clips with the pill as it collapses; the content
                row's own z keeps the labels above it. */}
            {linkHighlight}
            <div
              ref={contentRowRef}
              className="relative left-full z-10 flex w-max -translate-x-full items-center gap-m"
            >
              <div className="relative grid shrink-0 items-center">
                <motion.div
                  ref={menuLayerRef}
                  aria-hidden={expanded}
                  inert={expanded}
                  initial={false}
                  animate={{ opacity: expanded ? 0 : 1 }}
                  transition={effectiveMorphTransition}
                  className={cn(
                    "col-start-1 row-start-1 w-max justify-self-end",
                    expanded && "pointer-events-none",
                  )}
                >
                  <button
                    ref={menuButtonRef}
                    type="button"
                    aria-label="Open menu"
                    aria-expanded={expanded}
                    data-nav-control="menu"
                    onClick={onMenuClick}
                    className="relative flex size-xl items-center justify-center text-content-primary"
                  >
                    {/* Nudged 4px right of true centre. The 12px mark sits in a
                        32px hit target, so true centre leaves 18px to the
                        pill's edge against 26px to Shop (10px of centring plus
                        the row's 16px gap); the nudge evens both to 22px
                        without moving the target or the pill's width. */}
                    <MenuIcon className="translate-x-1" />
                  </button>
                </motion.div>
                <motion.div
                  ref={linksLayerRef}
                  aria-hidden={!expanded}
                  inert={!expanded}
                  initial={false}
                  animate={{ opacity: expanded ? 1 : 0 }}
                  transition={effectiveMorphTransition}
                  className={cn(
                    "col-start-1 row-start-1 w-max",
                    !expanded && "pointer-events-none",
                  )}
                >
                  {links}
                </motion.div>
              </div>
              <div ref={rightControlsRef} className="shrink-0">
                {rightControls({ cartCount, onCartClick })}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </DesktopNavLinks>
  );
};

export default FloatingNavSurface;
